import { useEffect, useId, useRef, useState, type FC } from 'react';
import { createPortal } from 'react-dom';
import { Spinner } from 'react-bootstrap';
import { cn } from '@/lib/utils';
import useDomain from '@/hooks/useDomain';
import { toMcpApiErrorMessage, useGetMcpAutoBudgetQuery } from '../api/mcpApi';
import { McpModelPicker } from './McpModelPicker';
import { McpDomainPicker } from './McpDomainPicker';
import { menuStyleAbove } from './McpSearchMenu';

const SIZE = 16;
const STROKE = 1.75;
const RADIUS = (SIZE - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

function formatFaNumber(value: number): string {
  return value.toLocaleString('fa-IR');
}

/** Same rounding as the auto-budget API: partial hours count, never zero. */
function hoursUntilBudgetReset(
  hoursUntilReset: number,
  resetAt: string
): number | null {
  if (Number.isFinite(hoursUntilReset)) return hoursUntilReset;
  const reset = new Date(resetAt);
  if (Number.isNaN(reset.getTime())) return null;
  const remainingMs = reset.getTime() - Date.now();
  return Math.max(1, Math.ceil(remainingMs / 3_600_000));
}

function budgetPercent(used: number, limit: number): number {
  if (limit <= 0) return 0;
  return Math.min(100, Math.max(0, (used / limit) * 100));
}

function ringColor(percent: number, exhausted: boolean): string {
  if (exhausted || percent >= 90) return 'var(--bs-danger, #e63757)';
  if (percent >= 70) return 'var(--bs-warning, #f5803e)';
  return 'currentColor';
}

/** Cursor-style context ring. Fill is the daily Auto budget; click shows the numbers. */
export const McpContextBudgetRing: FC = () => {
  const { domain_slug: domainSlug } = useDomain();
  const { data, isLoading, error } = useGetMcpAutoBudgetQuery(
    domainSlug ?? '',
    { skip: !domainSlug, refetchOnMountOrArgChange: true }
  );
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<DOMRect | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  const percent = data ? budgetPercent(data.used, data.limit) : 0;
  const resetHours = data
    ? hoursUntilBudgetReset(data.hoursUntilReset, data.resetAt)
    : null;
  const exhausted = data ? data.remaining <= 0 : false;
  const dash = percent >= 100 ? CIRCUMFERENCE : (percent / 100) * CIRCUMFERENCE;
  const loading = Boolean(domainSlug) && isLoading && !data;

  useEffect(() => {
    if (!open) return;

    const place = () => {
      setAnchor(buttonRef.current?.getBoundingClientRect() ?? null);
    };
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (buttonRef.current?.contains(target)) return;
      if (popoverRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    place();
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);

  const toggle = () => {
    setAnchor(buttonRef.current?.getBoundingClientRect() ?? null);
    setOpen(prev => !prev);
  };

  const label = data
    ? `بودجهٔ روزانه Auto، ${formatFaNumber(Math.round(percent))} درصد مصرف شده`
    : 'بودجهٔ روزانه Auto';

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={cn(
          'mcp-context-ring inline-flex size-6 shrink-0 items-center justify-center self-center rounded-full border-0 bg-transparent p-0',
          'text-muted-foreground hover:bg-muted-foreground/15 hover:text-foreground',
          open && 'bg-muted-foreground/15 text-foreground'
        )}
        aria-label={label}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? titleId : undefined}
        onClick={toggle}
      >
        <svg
          width={14}
          height={14}
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          aria-hidden
          className={loading ? 'opacity-40' : undefined}
        >
          <circle
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={RADIUS}
            fill="none"
            stroke="currentColor"
            strokeOpacity={0.28}
            strokeWidth={STROKE}
          />
          {dash > 0 && (
            <circle
              cx={SIZE / 2}
              cy={SIZE / 2}
              r={RADIUS}
              fill="none"
              stroke={ringColor(percent, exhausted)}
              strokeWidth={STROKE}
              strokeLinecap={percent >= 100 ? 'butt' : 'round'}
              strokeDasharray={`${dash} ${CIRCUMFERENCE - dash}`}
              transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
            />
          )}
        </svg>
      </button>

      {open && anchor
        ? createPortal(
            <div
              ref={popoverRef}
              id={titleId}
              role="dialog"
              aria-label="بودجهٔ روزانه Auto"
              dir="rtl"
              style={menuStyleAbove(anchor, {
                width: 240,
                align: 'left',
                maxHeightCap: 220
              })}
              className="mcp-budget-popover overflow-hidden rounded-3 border bg-white px-3 py-2 text-body shadow"
            >
              <div className="d-flex align-items-center justify-content-between gap-2">
                <span className="fw-semibold fs--1">بودجهٔ روزانه Auto</span>
                {data && !error && (
                  <span className="fs--2 text-600">
                    {formatFaNumber(Math.round(percent))}٪
                  </span>
                )}
              </div>

              {!domainSlug && (
                <p className="fs--2 text-600 mb-0 mt-2">
                  برای دیدن بودجهٔ روزانه ابتدا یک دامنه انتخاب کنید.
                </p>
              )}

              {domainSlug && loading && (
                <div className="d-flex justify-content-center py-2">
                  <Spinner animation="border" size="sm" />
                </div>
              )}

              {domainSlug && error && (
                <p className="fs--2 text-danger mb-0 mt-2">
                  {toMcpApiErrorMessage(error, 'خطا در دریافت بودجهٔ روزانهٔ Auto')}
                </p>
              )}

              {domainSlug && data && !error && (
                <>
                  <div
                    className="progress mt-2"
                    style={{ height: 4 }}
                    role="progressbar"
                    aria-valuemin={0}
                    aria-valuemax={data.limit}
                    aria-valuenow={Math.min(
                      Math.max(data.used, 0),
                      Math.max(data.limit, 0)
                    )}
                    aria-label="مصرف بودجه روزانه Auto"
                  >
                    <div
                      className={cn(
                        'progress-bar',
                        exhausted && 'bg-danger',
                        !exhausted && percent >= 70 && 'bg-warning'
                      )}
                      style={{ width: `${percent}%` }}
                    />
                  </div>
                  <div className="fs--1 mt-2">
                    {formatFaNumber(data.used)} از {formatFaNumber(data.limit)}{' '}
                    <span className={cn('fs--2', exhausted ? 'text-danger' : 'text-600')}>
                      (
                      {exhausted
                        ? 'سهمیه تمام شده'
                        : `${formatFaNumber(data.remaining)} باقی‌مانده`}
                      )
                    </span>
                  </div>
                  {data.inFlight > 0 && (
                    <div className="fs--2 text-600">
                      {formatFaNumber(data.inFlight)} در حال انجام
                    </div>
                  )}
                  {resetHours != null && (
                    <div className="fs--2 text-600 mt-1">
                      بودجه {formatFaNumber(resetHours)} ساعت دیگر ریست می‌شود
                    </div>
                  )}
                </>
              )}
            </div>,
            document.body
          )
        : null}
    </>
  );
};

/** Model picker, rendered before send. */
export const McpComposerBeforeSend: FC = () => <McpModelPicker />;

/** Bottom composer bar: domain picker, then the budget ring on its left. */
export const McpComposerFooter: FC = () => (
  <>
    <McpDomainPicker />
    <McpContextBudgetRing />
  </>
);
